function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/* eslint-env browser */

import isString from 'lodash/isString';
import merge from 'lodash/merge';

import { randomString } from './utils';
import EventEmitter from './eventEmitter';
import { cookieConfig } from './cookie-config';

var PULSE_COOKIE_KEY = cookieConfig.PULSE_COOKIE_KEY,
    TEST_COOKIE_KEY = cookieConfig.TEST_COOKIE_KEY,
    ENV_COOKIE_KEY = cookieConfig.ENV_COOKIE_KEY,
    USE_SECURE_COOKIES = cookieConfig.USE_SECURE_COOKIES;


var CIS_POLL_INTERVAL = 900000; // 15 minutes
var APP_NEXUS_SYNC_INTERVAL = 14400000; // 4 hours


/**
 * @private
 */
function getExpiryDate() {
  var expiresInDays = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1;

  var expireTimestamp = Date.now() + expiresInDays * 1000 * 60 * 60 * 24;
  return new Date(expireTimestamp);
}

/**
 * @private
 *
 * Used by unit tests
 */
/* eslint function-paren-newline: 0 */
/* eslint max-len: [2, 130] */
export function readCookie(key) {
  return decodeURIComponent(document.cookie.replace(new RegExp('(?:(?:^|.*;)\\s*' + encodeURIComponent(key).replace(/[-.+*]/g, '\\$&') + '\\s*\\=\\s*([^;]*).*$)|^.*$'), '$1'));
}

/**
 * Responsible for subscribing to, and emitting events.
 */
export var eventEmitter = new EventEmitter();

/**
 * @private
 *
 * Used by unit tests
 */
export function writeCookie(key, value, domain) {
  var expiresInDays = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 1;

  var expires = getExpiryDate(expiresInDays).toUTCString();
  var cookieString = '';
  cookieString = key + '=' + encodeURIComponent(value);
  cookieString += ';path=/';
  if (domain) {
    cookieString += ';domain=' + domain;
  }
  if (expiresInDays) {
    cookieString += ';expires=' + expires;
  }
  if (USE_SECURE_COOKIES && window.location.protocol === 'https:') {
    cookieString += ';SameSite=None;Secure';
  }
  document.cookie = cookieString;
}

/**
 * @private
 *
 * Used in unit tests, otherwise private
 */
export function deleteCookie(key, domain) {
  writeCookie(key, '', domain, -1);
}

/* eslint no-empty: 0 */
export function getRootDomain() {
  var parts = ('' + window.location.hostname).split('.');
  var testValue = randomString();
  var domain = window.location.hostname;
  var test = '';
  for (var i = 0; i < parts.length; i += 1) {
    try {
      domain = parts.slice(-(i + 1)).join('.');
      writeCookie(TEST_COOKIE_KEY, testValue, domain, 1);
      test = readCookie(TEST_COOKIE_KEY);
      deleteCookie(TEST_COOKIE_KEY, domain);
      if (test === testValue) {
        break;
      }
    } catch (e) {}
  }
  return domain;
}

export function cacheOldEnvironmentId(id) {
  try {
    var envCookie = '';
    var oldIds = {};
    try {
      envCookie = readCookie(ENV_COOKIE_KEY);
    } catch (e) {}
    try {
      oldIds = envCookie ? JSON.parse(envCookie) : [];
    } catch (e) {}

    oldIds[id] = Date.now();
    var idsToCache = Object.keys(oldIds).map(function (envId) {
      return { id: envId, date: oldIds[envId] };
    }).sort(function (o) {
      return o.date;
    }).slice(0, 5).reduce(function (accl, o) {
      return merge(accl, _defineProperty({}, o.id, o.date));
    }, {});
    writeCookie(ENV_COOKIE_KEY, JSON.stringify(idsToCache), getRootDomain(), 365);
  } catch (e) {}
}

export function parsePulseCookies() {
  var cookie = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : readCookie(PULSE_COOKIE_KEY);

  if (!isString(cookie)) {
    throw new Error('This method expects a cookie *string*');
  }
  var values = isString(cookie) ? decodeURIComponent(cookie).split(',') : [];
  // Cookie is in a unknown, possibly old format. Will be ignored.
  if (values.length !== 10) {
    values = [];
  }
  var altEnvCookie = readCookie(ENV_COOKIE_KEY);
  var altEnvIds = void 0;
  if (altEnvIds) {
    try {
      altEnvIds = JSON.parse(altEnvCookie);
    } catch (e) {
      altEnvIds = undefined;
    }
  }
  return {
    environmentId: values[0],
    prevUserId: values[2] ? values[2] : undefined,
    validateAfter: values[3] ? parseInt(values[3], 10) : undefined,
    jwe: values[4],
    anId: values[5],
    syncWithAppNexus: values[6] ? parseInt(values[6], 10) : undefined,
    doTracking: values[7] ? values[7] === 'true' : undefined,
    pixelInjected: values[8] ? values[8] === 'true' : undefined,
    adId: values[9],
    altEnvIds: altEnvIds,
    visitorIdPlaceholder: values[1]
  };
}

export function serializePulseCookie(input) {
  if (isString(input)) {
    throw new Error('This method expects a parsed cookie object, not a string');
  }

  var _ref = input || {},
      _ref$environmentId = _ref.environmentId,
      environmentId = _ref$environmentId === undefined ? '' : _ref$environmentId,
      _ref$prevUserId = _ref.prevUserId,
      prevUserId = _ref$prevUserId === undefined ? '' : _ref$prevUserId,
      _ref$jwe = _ref.jwe,
      jwe = _ref$jwe === undefined ? '' : _ref$jwe,
      _ref$anId = _ref.anId,
      anId = _ref$anId === undefined ? '' : _ref$anId,
      _ref$doTracking = _ref.doTracking,
      doTracking = _ref$doTracking === undefined ? '' : _ref$doTracking,
      _ref$pixelInjected = _ref.pixelInjected,
      pixelInjected = _ref$pixelInjected === undefined ? '' : _ref$pixelInjected,
      _ref$adId = _ref.adId,
      adId = _ref$adId === undefined ? '' : _ref$adId,
      _ref$visitorIdPlaceho = _ref.visitorIdPlaceholder,
      visitorIdPlaceholder = _ref$visitorIdPlaceho === undefined ? 'v' : _ref$visitorIdPlaceho;

  var now = Date.now();
  var cookieString = [environmentId, visitorIdPlaceholder, prevUserId, new Date().setTime(now + CIS_POLL_INTERVAL), jwe, anId, anId ? new Date().setTime(now + APP_NEXUS_SYNC_INTERVAL) : 0, doTracking, pixelInjected, adId].join(',');

  writeCookie(PULSE_COOKIE_KEY, cookieString, getRootDomain(), 2 * 365);
  eventEmitter.emit('environmentIdSet', environmentId);
  eventEmitter.emit('jweSet', jwe);
}

export function getSyncState(userId) {
  var cookie = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : readCookie(PULSE_COOKIE_KEY);

  var parsedCookie = parsePulseCookies(cookie);
  var dateNow = Date.now();
  var state = {};

  if (!cookie || cookie.indexOf('undefined') !== -1 || !parsedCookie.environmentId || !parsedCookie.jwe) {
    state.syncWithCis = true;
    state.waitForCis = true;
  } else if (String(userId) !== String(parsedCookie.prevUserId)) {
    state.syncWithCis = true;
    // Blocking call on login/new user to get opt out status, non blocking call
    // on logout to the a new JWE
    state.waitForCis = Boolean(userId);
  } else if (parsedCookie.validateAfter && dateNow > parsedCookie.validateAfter) {
    state.syncWithCis = true;
  }

  if (parsedCookie.syncWithAppNexus && dateNow > parsedCookie.syncWithAppNexus || !parsedCookie.anId) {
    state.syncWithAppNexus = true;
  }
  return state;
}